export const CREDITS_DASHBOARD_PARAM = 'creditsDashboard';

/** The polus unifier route (BEFE) in front of aica's POST /usage/summary, and the ServiceDefinition id its answer is keyed by. */
export const USAGE_SUMMARY_ENDPOINT = '/unifier/getUsageSummary';
export const USAGE_SUMMARY_SERVICE_ID = 'AICA_USAGE_SUMMARY';

export const CREDITS_COPY = {
    DASHBOARD_TITLE: 'Usage Dashboard',
    LOAD_FAILED: "Couldn't load usage",
    LOAD_FAILED_FOR: "Couldn't load usage for",
    SHOWING: 'Showing',
    USAGE_UNAVAILABLE: 'Usage temporarily unavailable',
    NO_PRICING: 'No pricing set',
    PRICING_UNAVAILABLE: 'Pricing temporarily unavailable',
    NOT_PRICED: 'Not priced',
    CURRENT_RATES_NOTE: "Priced at current rates: today's prices apply to every day in the selected range.",
    UNAVAILABLE: 'Unavailable',
    BAR_LABEL_PREFIX: 'Cost split',
    BAR_TINY_SHARE: '<1%',
    MODULE_HEADER: 'Module',
    PRICE_HEADER: 'Price',
    ENTITY_TYPE_HEADER: 'Entity Type',
    NEW_HEADER: 'New',
    REFRESH_HEADER: 'Refresh',
    USAGE_HEADER: 'Usage',
    TOTAL_COST_HEADER: 'Total Cost',
    TOTAL: 'Total',
    GRAND_TOTAL: 'Grand Total',
    VARIES: 'Varies',
    DASH: '–',
    EXCLUDES_UNPRICED_NOTE: '* Excludes usage that has no price yet',
    FROM_LABEL: 'From date',
    TO_LABEL: 'To date',
    RANGE_TO: 'to',
    AVAILABLE_FROM_PREFIX: 'Usage is available from',
    INVALID_RANGE: 'Choose a start date on or before the end date, within 366 days',
    PRESET_GROUP_LABEL: 'Date range presets',
    PRESET: { LAST_MONTH: 'Last Month', CURRENT_MONTH: 'Current Month', LAST_QUARTER: 'Last Quarter' },
    REPORTS_SECTION: 'Reports Usage Breakdown',
    DATA_SECTION: 'Data Usage Breakdown',
    SERVICES_SECTION: 'Services Usage Breakdown',
    RETRY: 'Retry',
    CHART_EMPTY: 'No usage in this range',
    CHART_LABEL: 'Daily usage cost',
    INCOMPLETE_DAY_NOTE: '* Excludes modules that are unavailable or not fully priced',
    DATE_HEADER: 'Date',
    DOWNLOAD: 'Download',
    DOWNLOAD_ARIA: 'Download the AICA Usage Summary for this range, with a detail sheet for each module',
    ASK_AICA: { LABEL: 'Ask AICA', PRICE: 'N.A.', EMPTY: '-' },
    WORKBOOK: {
        TITLE: 'AICA Usage Summary',
        FILE_PREFIX: 'AICA_Usage_Summary',
        OVERVIEW_SHEET: 'Overview',
        DAILY_SHEET: 'Daily',
        PNO: 'PNO',
        DATA_FROM: 'Data From',
        DATA_TILL: 'Data Till',
        GENERATED_AT: 'Generated At',
        STATUS: 'Status',
        PRICING: 'Pricing',
        STATUS_COMPLETE: 'Complete',
        STATUS_INCOMPLETE: 'Incomplete: some modules are unavailable or not fully priced; their totals show –',
        IST_NOTE: 'All dates are IST (Asia/Kolkata).',
        USAGE_SUFFIX: 'Usage',
        COST_SUFFIX: 'Total Cost',
    },
    DETAIL: {
        SHEET: {
            REPORTS: 'Reports Usage',
            GST: 'GST Usage',
            BANKING: 'Banking Usage',
            MAGIC_UPLOAD: 'Magic Upload Usage',
            DIRECTOR_BUREAU: 'Director Bureau Usage',
            LEGAL_RISK: 'Legal & Risk Usage',
            MAGIC_SIGN: 'Magic Sign Usage',
            SITE_VISIT: 'Site Visit Usage',
        },
        COLUMN: {
            DATE: 'Date',
            ORG_NAME: 'Organisation Name',
            ORG_PAN: 'Organisation PAN',
            TYPE: 'Type',
            ENTITY_TYPE: 'Entity Type',
            TIER: 'Tier',
            ENTITY_NAME: 'Entity Name',
            CHECK: 'Check',
            USAGE: 'Usage',
            BUREAU: 'Bureau',
            GST_NUMBER: 'GST Number',
            DURATION_MONTHS: 'Duration (months)',
            UNITS: 'Units',
            ACCOUNT: 'Account',
            DOCUMENT_NAME: 'Document Name',
            DOCUMENT_TYPE: 'Document Type',
            SIGN_TYPE: 'Sign Type',
            VISITS: 'Visits',
            UNIT_PRICE: 'Unit Price',
            TOTAL: 'Total',
        },
        DATA_FROM: 'Data From:',
        DATA_TILL: 'Data Till',
        STATUS_TEXT: {
            UNAVAILABLE: 'Usage unavailable for this period; not itemised.',
            UNIT_NOT_SUPPORTED: "This module's unit isn't supported; not itemised.",
            DETAIL_ABSENT: 'Detail unavailable for this download; not itemised.',
            NO_USAGE: 'No usage in this period.',
        },
        TOO_LARGE: (rowCount: number | null, maxRows: number | null) =>
            `${rowCount} line items: too many to itemise in one file (limit ${maxRows}). Narrow the date range to list them.`,
        REPORTS_STATUS_PREFIX: { REPORTS: 'Reports: ', LEADS_ADDED: 'Added, not unlocked: ' },
        NOT_LINKED_ORG: 'Not linked to a company',
        UNKNOWN_ORG: 'Unknown company',
        ACCOUNT_NOT_IDENTIFIED: 'Account not identified',
        NAME_NOT_AVAILABLE: 'Name not available',
        NOT_RECORDED: 'Not recorded',
        REPORT_TYPE: { NEW: 'New', REFRESH: 'Refresh', ADDED_NOT_UNLOCKED: 'Added, not unlocked' },
        BUREAU_LABEL: { TRANSUNION: 'TransUnion CIBIL', EXPERIAN: 'Experian', EQUIFAX: 'Equifax' },
        CHECK_LABEL: { AML_PEP: 'AML/PEP', LITIGATION: 'Litigation' },
        SIGN_TYPE_LABEL: { E_SIGN: 'E-sign', AADHAAR: 'Aadhaar e-sign', DSC: 'DSC' },
        DOWNLOAD_TOOLTIP: 'Downloads an Excel file with an overview, a daily sheet and a detail sheet for each module.',
        DOWNLOAD_FAILED: "Couldn't prepare the download. Please try again.",
    },
} as const;

export const MODULE_KEY = {
    LEADS_ADDED: 'LEADS_ADDED',
    REPORTS: 'REPORTS',
    GST: 'GST',
    BANKING: 'BANKING',
    MAGIC_UPLOAD: 'MAGIC_UPLOAD',
    DIRECTOR_BUREAU: 'DIRECTOR_BUREAU',
    LEGAL_RISK: 'LEGAL_RISK',
    MAGIC_SIGN: 'MAGIC_SIGN',
    SITE_VISIT: 'SITE_VISIT',
} as const;

export const SECTION = {
    REPORTS: 'REPORTS',
    DATA: 'DATA',
    SERVICES: 'SERVICES',
} as const;

export const SECTION_ORDER = [SECTION.REPORTS, SECTION.DATA, SECTION.SERVICES] as const;

export const SECTION_TITLE = {
    REPORTS: CREDITS_COPY.REPORTS_SECTION,
    DATA: CREDITS_COPY.DATA_SECTION,
    SERVICES: CREDITS_COPY.SERVICES_SECTION,
} as const;

export const MODULE_STATUS = {
    OK: 'OK',
    UNAVAILABLE: 'UNAVAILABLE',
    UNIT_NOT_SUPPORTED: 'UNIT_NOT_SUPPORTED',
    TOO_LARGE: 'TOO_LARGE',
} as const;

export const BREAKDOWN_COLUMNS = { MODULE: 'MODULE', ENTITY_NEW_REFRESH: 'ENTITY_NEW_REFRESH' } as const;

export const TOOLTIP_COLUMNS = { USAGE: 'USAGE', NEW_REFRESH: 'NEW_REFRESH' } as const;

export const REPORT_TYPE = { NEW: 'NEW', REFRESH: 'REFRESH' } as const;

export const MODULE_PRICING_STATUS = { PRICED: 'PRICED', PARTIAL: 'PARTIAL', NOT_PRICED: 'NOT_PRICED' } as const;

export const SUMMARY_PRICING_STATUS = { OK: 'OK', NO_PRICING: 'NO_PRICING', PRICING_UNAVAILABLE: 'PRICING_UNAVAILABLE' } as const;

export const MODULE_LABEL = {
    LEADS_ADDED: 'Added Not Unlocked',
    REPORTS: 'Reports',
    GST: 'GST',
    BANKING: 'Banking',
    MAGIC_UPLOAD: 'Magic Upload',
    DIRECTOR_BUREAU: 'Director Bureau',
    LEGAL_RISK: 'Legal & Risk Checks',
    MAGIC_SIGN: 'Magic Sign',
    SITE_VISIT: 'Site Visits',
} as const;

/** Module colours for the table dots, cost-share bars and chart tooltip dots (Figma 4631:96055, 4757:108397). */
export const MODULE_COLOR = {
    LEADS_ADDED: '#9D9D9D',
    REPORTS: '#A472E5',
    GST: '#B2EE95',
    BANKING: '#FED762',
    MAGIC_UPLOAD: '#545B62',
    DIRECTOR_BUREAU: '#FFB578',
    LEGAL_RISK: '#B2EAFF',
    MAGIC_SIGN: '#BEBBFF',
    SITE_VISIT: '#89C0FF',
} as const;

/** The seven report entity types in the order the response sends them. */
export const REPORT_ENTITY_KEYS = [
    'PUBLIC_LIMITED',
    'PRIVATE_LIMITED',
    'LLP',
    'PARTNERSHIP',
    'PROPRIETORSHIP',
    'INDIVIDUAL',
    'OTHERS',
] as const;

/** Shared byte-for-byte with aica-admin's pricing editor. */
export const REPORT_ENTITY_LABEL = {
    PUBLIC_LIMITED: 'Public Companies',
    PRIVATE_LIMITED: 'Private Limited Companies',
    LLP: 'LLP Companies',
    PARTNERSHIP: 'Partnerships',
    PROPRIETORSHIP: 'Sole Proprietorships',
    INDIVIDUAL: 'Individuals',
    OTHERS: 'Others Entity Types',
} as const;

/** Purple shades for the companies, blue shades for the rest (Figma 4757:78949 row dots). */
export const REPORT_ENTITY_COLOR = {
    PUBLIC_LIMITED: '#A472E5',
    PRIVATE_LIMITED: '#BF9CED',
    LLP: '#DBC7F5',
    PARTNERSHIP: '#2F8FFF',
    PROPRIETORSHIP: '#59A5FF',
    INDIVIDUAL: '#82BCFF',
    OTHERS: '#ACD2FF',
} as const;
export const ASK_AICA_KEY = 'ASK_AICA';
export const ASK_AICA_COLOR = '#FF91F7';

/** Per billing unit: the [singular, plural] noun for usage counts and the suffix after a unit price. */
export const UNIT_LABEL = {
    LEAD: { noun: ['lead', 'leads'], priceSuffix: '/Lead' },
    REPORT: { noun: ['report', 'reports'], priceSuffix: '/Report' },
    FETCH: { noun: ['fetch', 'fetches'], priceSuffix: '/Fetch' },
    GSTIN_MONTH: { noun: ['GSTIN-month', 'GSTIN-months'], priceSuffix: '/GSTIN-Month' },
    GSTIN_YEAR: { noun: ['GSTIN-year', 'GSTIN-years'], priceSuffix: '/GSTIN-Year' },
    ACCOUNT: { noun: ['account', 'accounts'], priceSuffix: '/Account' },
    STATEMENT: { noun: ['statement', 'statements'], priceSuffix: '/Statement' },
    DOC_TAGGED: { noun: ['doc tagged', 'docs tagged'], priceSuffix: '/Doc' },
    ENTITY: { noun: ['entity', 'entities'], priceSuffix: '/Entity' },
    DOC_SIGNED: { noun: ['doc signed', 'docs signed'], priceSuffix: '/Doc' },
    VISIT: { noun: ['visit', 'visits'], priceSuffix: '/Visit' },
} as const;

/** Reports priceBy values where the per-unit price varies by tier, so an entity row has no single price. */
export const TIER_PRICE_BY: readonly string[] = ['TIER', 'TIER_AND_ENTITY_TYPE'];

export const TIER_LABEL = { TIER_1: 'Tier 1', TIER_2: 'Tier 2', TIER_3: 'Tier 3' } as const;

export const CREDITS_PRESET = {
    LAST_MONTH: 'LAST_MONTH',
    CURRENT_MONTH: 'CURRENT_MONTH',
    LAST_QUARTER: 'LAST_QUARTER',
} as const;

export const MAX_RANGE_DAYS = 366;

/** First IST day the dashboard has data for; used until the first response supplies `availableFrom`. */
export const AVAILABLE_FROM_FALLBACK = '2026-10-01';

export const CHART_STROKE_COLOR = '#6021B3';
export const CHART_HOVER_LINE_COLOR = '#9D9D9D';
export const CHART_AXIS_TEXT_COLOR = '#6E7880';
export const CHART_GRID_COLOR = '#E7E7E7';
export const CHART_AREA_TOP_COLOR = '#C599FF';
export const CHART_AREA_BOTTOM_COLOR = '#E586FF';
